// 10.19 A customer answered "How did we do?" with okay or unhappy: the owner gets in touch and puts it right. Their
// comment is their own words, shown as text. Their review request is not affected: it goes to every customer, whatever
// they answer (no review gating, docs/COMPLIANCE.md).
import { useId, useState } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { ANSWER_LABEL, type Answer } from "../../lib/happiness";
import { Notice } from "../../components/ui";

/** The one-line summary on Home. */
export function putItRightSummary(t: HumanTask): string {
  const answer = ANSWER_LABEL[t.payload.answer as Answer] ?? "Not happy";
  return t.payload.title ? String(t.payload.title) : `A customer answered ${answer.toLowerCase()}: put it right`;
}

export function PutItRightTask({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const answer = ANSWER_LABEL[task.payload.answer as Answer] ?? "Not happy";
  const person = typeof task.payload.person_id === "string" ? task.payload.person_id : null;
  const comment = typeof task.payload.comment === "string" ? task.payload.comment : null;
  return (
    <div>
      <p>
        They answered <strong>{answer}</strong> to “How did we do?”.
      </p>
      {comment ? (
        <blockquote className="quote">{comment}</blockquote>
      ) : (
        <p className="muted small">They left no comment.</p>
      )}
      <p className="muted small">
        Get in touch and put it right. Their review request is not affected: the same request goes to every customer,
        at the same time and with the same words, whatever they answered.
      </p>
      {person && (
        <p>
          <Link to={`/contacts/people/${person}`}>Open their page</Link>
        </p>
      )}
      <label className="field" htmlFor={id}>
        <span className="field-label">What you did (optional)</span>
        <textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await crm.completeTask(task.id, "done", note.trim() ? { note: note.trim().slice(0, 2000) } : {});
              onDone();
            } catch (e) {
              setError((e as Error).message);
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : "Done: I've been in touch"}
        </button>
      </div>
    </div>
  );
}
